
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getTasks } from "../api/taskapi";
import { toast } from "react-toastify";
import { FaBoxes, FaArrowLeft, FaProjectDiagram } from "react-icons/fa";
import { FiPlusCircle } from "react-icons/fi";
import { getActivities } from "../api/activityApi";
import Sidebar from "../components/reusablefieldbar";
import Topbar from "../components/TopbarNav";
import menuItems from "../menuitems/menuItems";

import AddResourceModal from "../components/AddResourceModal";

const LoadingSpinner = () => (
    <div className="flex justify-center items-center py-10">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
    </div>
);

const ActivityResourcesView = () => {
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activity, setActivity] = useState(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const activityId = location.state?.activityId;

    const fetchResources = async () => {
        setLoading(true);
        setError(null);
        try {
            const [allTasks, activities] = await Promise.all([
                getTasks(),
                getActivities()
            ]);

            const tasksForActivity = allTasks.filter(
                (task) => task.activity?._id === activityId
            );
            
            const currentActivity = activities.find(act => act._id === activityId);
            setActivity(currentActivity);

            const consolidatedResources = {};
            tasksForActivity.forEach(task => {
                if (task.resources && task.resources.length > 0) {
                    task.resources.forEach(resource => {
                        const trimmedResource = resource.trim();
                        if (trimmedResource) {
                            if (!consolidatedResources[trimmedResource]) {
                                consolidatedResources[trimmedResource] = [];
                            }
                            consolidatedResources[trimmedResource].push(task);
                        }
                    });
                }
            });

            const sortedResources = Object.keys(consolidatedResources).sort();
            
            setResources(sortedResources.map(key => ({
                name: key,
                tasks: consolidatedResources[key],
            })));

        } catch (err) {
            console.error("Failed to fetch resources:", err);
            setError("Failed to load resources for this activity.");
            toast.error("Failed to load data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!activityId) {
            toast.error("No activity selected. Redirecting to tasks...");
            setTimeout(() => navigate('/tasks'), 2000);
            return;
        }
        fetchResources();
    }, [activityId, navigate]);

    const handleAddModalOpen = () => setIsAddModalOpen(true);
    const handleAddModalClose = () => {
        setIsAddModalOpen(false);
        fetchResources(); 
    };

    return (
        <div className="flex h-screen bg-gray-50 font-sans">
            <Sidebar menuItems={menuItems} />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <main className="flex-1 p-6 overflow-y-auto pt-16">
                    <div className="max-w-7xl mx-auto">
                        <nav className="text-sm font-semibold text-gray-500 mb-4">
                            <span onClick={() => navigate('/tasks')} className="cursor-pointer hover:text-orange-600 transition-colors">
                                Task Management
                            </span>
                            <span className="mx-2">/</span>
                            <span className="text-gray-900">Resource Planning</span>
                        </nav>
                        <div className="flex items-center justify-between mb-6">
                            <h1 className="text-3xl font-bold text-gray-800">
                                <FaBoxes className="inline-block mr-2 text-orange-500" />
                                Resource Planning
                            </h1>
                            <div className="flex space-x-4">
                                <button
                                    onClick={handleAddModalOpen}
                                    className="flex items-center space-x-2 rounded-md px-4 py-2 font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors duration-200"
                                >
                                    <FiPlusCircle />
                                    <span>Add New Resource</span>
                                </button>
                                <button
                                    onClick={() => navigate(-1)}
                                    className="flex items-center px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                                >
                                    <FaArrowLeft className="mr-2" />
                                    Back
                                </button>
                            </div>
                        </div>
                        
                        {activity && (
                           <p className="text-2xl text-gray-600 mb-8">Resources for: <span className="font-semibold">{activity.name}</span></p>
                        )}

                        {loading ? (
                            <LoadingSpinner />
                        ) : error ? (
                            <div className="text-center text-red-500 py-10">{error}</div>
                        ) : resources.length === 0 ? (
                            <div className="text-center text-gray-500 py-10">No resources identified for this activity's tasks.</div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {resources.map((resource, index) => (
                                    <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-200">
                                        <h3 className="text-xl font-bold text-gray-800 flex items-center mb-4">
                                            <span className="bg-orange-100 text-orange-800 text-sm font-medium mr-2 px-3 py-1 rounded-full">{resource.tasks.length}</span>
                                            {resource.name}
                                        </h3>
                                        <div className="mt-4 space-y-4">
                                            {resource.tasks.map(task => (
                                                <div key={task._id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                    <p className="text-sm font-medium text-gray-700">
                                                        <FaProjectDiagram className="inline-block mr-2 text-gray-400" />
                                                        Task: <span className="font-semibold">"{task.title}"</span>
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </main>
            </div>
            <AddResourceModal
                isOpen={isAddModalOpen}
                onClose={handleAddModalClose}
            />
        </div>
    );
};

export default ActivityResourcesView;